import type { ConfirmModalStyle } from "../common/components/confirm-modal";
import type { Corners, ImageAspectRatio, ImageFitStyle, ImageGravity, Sizing } from "../common/components/image-types";
import type { PastAndOrFuture } from "../common/components/past-future";
import type { SeparatorSpacing } from "../common/components/separator-spacing";
import type { TextBoxJustify, TextBoxLineLimit, TextBoxStyle } from "../common/components/text-box-types";
import type { TitleAppearance } from "../common/components/title-appearance";
import type { ArrayScreenFormat } from "../common/description";
import type { Appearance } from "../component-utils/button-appearance";
import type { CardFloatStyle } from "../component-utils/card-types";
import type { ChoiceStyle } from "../component-utils/choice-style";
import type { ImagePickerSourceHint } from "../component-utils/image-picker-types";
import type { ListItemFlags } from "../component-utils/list-item-flags";
import type { Mood } from "../component-utils/mood";
import type { ProgressType } from "../component-utils/progress-types";
import type { SimpleTableRowStyle } from "../component-utils/simple-table-types";
import type { TextEntrySize } from "../component-utils/text-entry-types";
import type { TextSize, TextStyle } from "../component-utils/text-types";
import type { WireComponentKind } from "../wire/component-kind";
import type { Unbound, WireAction, WireComponent, WireEditableValue } from "../wire/types";
import type { WireListComponentGeneric } from "./fluent-components-spec";

export interface WireFieldComponentBase extends WireComponent {
    readonly placeholder: string;
    readonly title: string;
    readonly isRequired: boolean;
}

export interface WireTextFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.TextField;
    readonly value: WireEditableValue<string>;
    readonly minChars?: number | null;
    readonly maxChars?: number | null;
    readonly size?: TextEntrySize;
}

export interface WireNumberFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.NumberField;
    readonly value: WireEditableValue<number | undefined>;
    readonly minValue?: number | null;
    readonly maxValue?: number | null;
}

export interface WireEmailFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.EmailField;
    readonly value: WireEditableValue<string>;
}

export interface WirePhoneFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.PhoneField;
    readonly value: WireEditableValue<string>;
}

export interface WireDateTimeFieldComponent extends WireFieldComponentBase {
    readonly kind: WireComponentKind.DateTimeField;
    readonly value: WireEditableValue<Date | undefined>;
    readonly pastOrFuture: PastAndOrFuture;
    readonly withTime: boolean;
}

export interface WireFilePickerComponentBase extends WireComponent {
    readonly title?: string | null;
    readonly value: WireEditableValue<string>;
    readonly isBusy: WireEditableValue<boolean>;
    readonly isRequired: boolean;
}

export interface WireFilePickerComponent extends WireFilePickerComponentBase {
    readonly kind: WireComponentKind.FilePicker;
}

export interface WireImagePickerComponent extends WireFilePickerComponentBase {
    readonly kind: WireComponentKind.ImagePicker;
    readonly sourceHint: ImagePickerSourceHint;
}

export enum WireToggleKind {
    Switch = "switch",
    Checkbox = "checkbox",
    Favorite = "favorite",
}

export interface WireToggleComponent extends WireComponent {
    readonly kind: WireComponentKind.Toggle;
    readonly toggleKind: WireToggleKind;
    readonly title?: string | null;
    readonly description?: string | null;
    readonly value: WireEditableValue<boolean>;
    readonly isRequired: boolean;
}

export interface WireActionWithTitle {
    readonly title?: string | Unbound;
    readonly action?: WireAction | Unbound;
}

export interface WireCardsCollectionListItem {
    readonly title?: string;
    readonly subtitle?: string;
    readonly emphasis?: string;
    readonly body?: string;
    readonly image?: string;

    readonly action?: WireAction;

    readonly itemActions?: readonly WireActionWithTitle[];
}

export type WireDataGridColumnKind = "string" | "boolean" | "image-uri";

export interface WireDataGridColumn {
    readonly title: string;
    readonly kind: WireDataGridColumnKind;
}

export interface WireDataGridListComponent extends WireListComponentGeneric<ArrayScreenFormat.DataGrid> {
    readonly columns: readonly WireDataGridColumn[];

    readonly items: readonly (readonly (string | boolean)[])[];
}

// TODO: We should just give the `displayAs` to the frontend, and the selected
// index.  We'll just have to make it so that we translate the index to the
// selected value in the change callback.
export interface WireChoiceItem {
    readonly displayAs: string;
    readonly isSelected: boolean;
    readonly onChange: WireAction | undefined;
}

export interface WireChoiceComponent extends WireListComponentGeneric<ArrayScreenFormat.Choice> {
    readonly title: string;
    readonly style: ChoiceStyle;
    // Only used for dropdown.
    readonly selectedValueText: string;

    readonly isMulti: boolean;
    readonly isRequired: boolean;

    readonly items: readonly WireChoiceItem[];
}

export interface WireListItem {
    readonly title: string | null;
    readonly subtitle: string | null;
    readonly image: string | null;
    readonly action: WireAction | undefined;
}

export interface WireListListItem extends WireListItem {
    readonly caption: string | null;
    readonly icon: string | null;
}

interface WireAppListComponent<TFormat extends ArrayScreenFormat> extends WireComponent {
    readonly kind: WireComponentKind.List;
    readonly format: TFormat;
    readonly title: string;
    readonly emptyMessage: string;
}

// FIXME: support grouping
export interface WireAppListListComponent<T extends ArrayScreenFormat.List | ArrayScreenFormat.SmallList>
    extends WireAppListComponent<T> {
    readonly allowWrapping: boolean;
    readonly flags: ListItemFlags;

    readonly items: readonly WireListListItem[];
}

export enum WireAppOverlayKind {
    Tag = "tag",
    Avatar = "avatar",
    ToggleIcon = "toggle-icon",
}

interface WireAppOverlay {
    readonly kind: WireAppOverlayKind;
}

export interface WireAppTagOverlay extends WireAppOverlay {
    readonly kind: WireAppOverlayKind.Tag;
    readonly text: string;
    readonly corners: Corners;
}

export interface WireAppAvatarOverlay extends WireAppOverlay {
    readonly kind: WireAppOverlayKind.Avatar;
    // Do we need to know whether these two are bound or not?
    readonly image?: string;
    readonly caption: string;
    readonly textSize: TextSize;
}

export interface WireAppToggleIconOverlay extends WireAppOverlay {
    readonly kind: WireAppOverlayKind.ToggleIcon;
    readonly value: WireEditableValue<boolean>;
}

export interface WireAppOverlays {
    readonly topLeft?: WireAppOverlay;
    readonly topRight?: WireAppOverlay;
    readonly bottomLeft?: WireAppOverlay;
    readonly bottomRight?: WireAppOverlay;
}

export interface WireTilesBaseListItem extends WireListItem {
    readonly overlays: WireAppOverlays;
}

export interface WireAppTilesBaseListComponent<TFormat extends ArrayScreenFormat, TItem extends WireTilesBaseListItem>
    extends WireAppListComponent<TFormat> {
    readonly size: ImageAspectRatio;
    readonly numColumns: number;
    readonly corners: Corners;
    readonly textSize: TextSize;
    readonly textStyle: TextStyle;
    readonly horizontal: boolean;

    readonly items: readonly TItem[];
}

export interface WireTilesListItem extends WireTilesBaseListItem {
    // FIXME: fallback initials
}

export interface WireAppTilesListComponent
    extends WireAppTilesBaseListComponent<ArrayScreenFormat.Tiles, WireTilesListItem> {
    readonly allowWrapping: boolean;
    readonly gravity: ImageGravity;
}

export interface WireCardsListItem extends WireTilesBaseListItem {
    readonly header: string | null;
}

export interface WireAppCardsListComponent
    extends WireAppTilesBaseListComponent<ArrayScreenFormat.Cards, WireCardsListItem> {
    readonly numTextLines: number;
    readonly floatStyle: CardFloatStyle;
}

export interface WireAppTinderComponent extends WireComponent {
    readonly kind: WireComponentKind.AppTinder;

    readonly top: readonly (WireComponent | null)[];
    readonly bottom?: readonly (WireComponent | null)[];

    readonly swipeLeftAction?: WireAction;
    readonly swipeRightAction?: WireAction;
}

export interface WireBreadcrumb {
    readonly title?: string | Unbound;
    readonly action?: WireAction;
}

export interface WireBreadcrumbsComponent extends WireComponent {
    readonly kind: WireComponentKind.Breadcrumbs;
    readonly currentPageTitle?: string | null;
    readonly crumbs: readonly WireBreadcrumb[];
    readonly crumbsOmitted: boolean;
    readonly backAction?: WireAction;
}

export interface WireSignInComponent extends WireComponent {
    readonly kind: WireComponentKind.SignIn;
    readonly onSuccess: WireAction;
    readonly onFailure: WireAction;
    readonly isSignUp: boolean;
}

export interface WireAppButton {
    readonly title: string;
    readonly mood: Mood;
    readonly icon?: string;
    readonly style: Appearance;
    readonly onTap?: WireAction;
}

export interface WireAppButtonComponent extends WireComponent, WireAppButton {
    readonly kind: WireComponentKind.AppButton;
    readonly confirm?: {
        readonly title: string;
        readonly description: string;
        readonly modalStyle: ConfirmModalStyle;
        readonly accept: string;
        readonly cancel: string;
    };
}

export interface WireAppButtonBarComponent extends WireComponent {
    readonly kind: WireComponentKind.AppButtonBar;
    // At least one of these will be defined
    readonly left?: WireAppButton;
    readonly right?: WireAppButton;
}

export interface WireAppTitleComponent extends WireComponent {
    readonly kind: WireComponentKind.AppTitle;
    readonly title?: string;
    readonly subtitle?: string;
    readonly image?: string;
    readonly appearance: TitleAppearance;
    readonly aspect?: ImageAspectRatio;
    readonly onTap?: WireAction;
}

export interface WireAppAdaptiveListItemComponent extends WireComponent {
    readonly kind: WireComponentKind.AppAdaptiveListItem;
    readonly title?: string;
    readonly subtitle?: string;
    readonly caption?: string;
    readonly image?: string;
    readonly icon?: string;
    readonly initials?: string;
    readonly onTap?: WireAction;
    readonly flags: ListItemFlags;
}

export interface WireAppImageComponent extends WireComponent {
    readonly kind: WireComponentKind.AppImage;
    readonly image: WireEditableValue<string>;
    readonly size: ImageAspectRatio;
    readonly sizing: Sizing;
    readonly gravity: ImageGravity;
    readonly style: ImageFitStyle;
    readonly withEnlarge: boolean;
    readonly onTap?: WireAction;
}

export interface WireAppSeparatorComponent extends WireComponent {
    readonly kind: WireComponentKind.AppSeparator;
    readonly spacing: SeparatorSpacing;
    readonly showLine: boolean;
}

export interface WireAppTextBoxComponent extends WireComponent {
    readonly kind: WireComponentKind.AppTextBox;
    readonly text: string;
    readonly style: TextBoxStyle;
    readonly justify: TextBoxJustify;
    readonly lineLimit: TextBoxLineLimit;
    readonly allowWrapping: boolean;
    readonly allCaps: boolean;
}

export interface WireAppPhoneNumberComponent extends WireComponent {
    readonly kind: WireComponentKind.AppPhoneNumber;
    readonly number: string;
    readonly onPhoneCall?: WireAction;
    readonly onTextMessage?: WireAction;
}

export interface WireAppSimpleTableRow {
    readonly label: string;
    readonly value: string;
    readonly style: SimpleTableRowStyle;
    readonly action?: WireAction;
}

export interface WireAppSimpleTableComponent extends WireComponent {
    readonly kind: WireComponentKind.AppSimpleTable;
    readonly caption?: string;
    readonly footer?: string;
    readonly allowsWrapping: boolean;
    readonly rows: readonly WireAppSimpleTableRow[];
}

export interface WireAppHintComponent extends WireComponent {
    readonly kind: WireComponentKind.AppHint;
    readonly title: string;
    readonly text: string;
    readonly mood: Mood;
    readonly justify: TextBoxJustify;
}

export interface WireAppMarkdownComponent extends WireComponent {
    readonly kind: WireComponentKind.AppMarkdown;
    readonly text: string;
    readonly onTap?: WireAction;
}

export interface WireAppAudioPlayerComponent extends WireComponent {
    readonly kind: WireComponentKind.AppAudioPlayer;
    readonly url: string;
}

export interface WireAppVideoPlayerComponent extends WireComponent {
    readonly kind: WireComponentKind.AppVideoPlayer;
    readonly url: string;
}

export interface WireAppProgressComponent extends WireComponent {
    readonly kind: WireComponentKind.AppProgress;
    readonly style: ProgressType;
    // TODO: Do we want a loading state when `value` is not loaded yet?
    readonly value: number;
    readonly formatted: string;
    readonly minimum: number;
    readonly maximum: number;
    readonly title: string;
    readonly caption: string;
    // Only valid for `Circle`
    readonly showTotal: boolean;
}

export interface WireAppLikeComponent extends WireComponent {
    readonly kind: WireComponentKind.AppLike;
    readonly caption: string;
    readonly value: WireEditableValue<number>;
}

export interface WireAppRatingComponent extends WireComponent {
    readonly kind: WireComponentKind.AppRating;
    readonly caption: string;
    readonly value: WireEditableValue<number>;
    readonly maxRating: number;
    readonly unit: string;
}

interface Reaction {
    readonly emoji: string;
    readonly value: string;
}

export interface WireAppReactionComponent extends WireComponent {
    readonly kind: WireComponentKind.AppReaction;
    readonly caption: string;
    readonly value: WireEditableValue<string>;
    readonly reactions: readonly Reaction[];
}
